// =====================================================================
// TopupBackfillPanel — admin reconciliation tool for AI credit top-ups
// that paid in Stripe but never landed in account_ai_credit_ledger
// (e.g. missed checkout.session.completed webhook).
//
// Calls supabase/functions/admin-credit-topup-backfill (op: scan, backfill).
// =====================================================================

import { useState } from "react";
import { Loader2, Search, Wrench } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type StripeEnv = "sandbox" | "live";

type ScanRow = {
  session_id: string;
  owner_user_id: string | null;
  pack_id: string | null;
  amount_total: number | null;
  currency: string | null;
  created_at: string;
};

type BackfillResultRow = {
  session_id: string;
  status: "credited" | "already_credited" | "failed";
  credits?: number;
  error?: string;
};

const fmtAmount = (cents: number | null, currency: string | null) => {
  if (cents == null) return "—";
  return `${(cents / 100).toFixed(2)} ${currency?.toUpperCase() ?? ""}`.trim();
};

export function TopupBackfillPanel() {
  const [env, setEnv] = useState<StripeEnv>("live");
  const [since, setSince] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [until, setUntil] = useState(() => new Date().toISOString().slice(0, 10));
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState<ScanRow[] | null>(null);
  const [scanned, setScanned] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [batchResults, setBatchResults] = useState<BackfillResultRow[] | null>(null);

  const runScan = async () => {
    setScanning(true);
    setResults(null);
    setBatchResults(null);
    setSelected(new Set());
    try {
      const { data, error } = await supabase.functions.invoke("admin-credit-topup-backfill", {
        body: {
          op: "scan",
          environment: env,
          since: new Date(since).toISOString(),
          until: new Date(`${until}T23:59:59Z`).toISOString(),
          limit: 200,
        },
      });
      if (error) throw error;
      const payload = data as { scanned: number; sessions: ScanRow[] };
      setResults(payload.sessions);
      setScanned(payload.scanned);
      toast.success(`Scanned ${payload.scanned} sessions, ${payload.sessions.length} missing`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!results) return;
    if (selected.size === results.length) setSelected(new Set());
    else setSelected(new Set(results.map((r) => r.session_id)));
  };

  const runBackfill = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-credit-topup-backfill", {
        body: {
          op: "backfill",
          environment: env,
          session_ids: Array.from(selected),
        },
      });
      if (error) throw error;
      const payload = data as {
        credited: number;
        already_credited: number;
        failed: number;
        results: BackfillResultRow[];
      };
      setBatchResults(payload.results);
      toast.success(
        `Backfill: ${payload.credited} credited, ${payload.already_credited} already, ${payload.failed} failed`,
      );
      setConfirmOpen(false);
      setConfirmText("");
      // Refresh — remove the credited sessions from the missing list.
      if (results) {
        const credited = new Set(
          payload.results.filter((r) => r.status === "credited" || r.status === "already_credited").map((r) => r.session_id),
        );
        setResults(results.filter((r) => !credited.has(r.session_id)));
      }
      setSelected(new Set());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Backfill failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="h-5 w-5" /> Top-up backfill
        </CardTitle>
        <CardDescription>
          Find paid AI credit top-up checkout sessions in Stripe whose credits never landed in the ledger
          (e.g. missed webhook), and credit them now. Idempotent — already-credited sessions are skipped.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div>
            <Label>Environment</Label>
            <Select value={env} onValueChange={(v) => setEnv(v as StripeEnv)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="live">live</SelectItem>
                <SelectItem value="sandbox">sandbox</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Since</Label>
            <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} />
          </div>
          <div>
            <Label>Until</Label>
            <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button onClick={runScan} disabled={scanning} className="w-full">
              {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Scan
            </Button>
          </div>
        </div>

        {results && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Scanned {scanned} session{scanned === 1 ? "" : "s"}. {results.length} missing from ledger.
            </p>
            {results.length > 0 && (
              <>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left">
                      <tr>
                        <th className="px-2 py-1.5">
                          <Checkbox
                            checked={selected.size === results.length && results.length > 0}
                            onCheckedChange={toggleAll}
                            aria-label="Select all"
                          />
                        </th>
                        <th className="px-2 py-1.5">Session</th>
                        <th className="px-2 py-1.5">Owner</th>
                        <th className="px-2 py-1.5">Pack</th>
                        <th className="px-2 py-1.5">Amount</th>
                        <th className="px-2 py-1.5">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r) => (
                        <tr key={r.session_id} className="border-t">
                          <td className="px-2 py-1.5">
                            <Checkbox
                              checked={selected.has(r.session_id)}
                              onCheckedChange={() => toggle(r.session_id)}
                              aria-label={`Select ${r.session_id}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 font-mono text-xs">{r.session_id}</td>
                          <td className="px-2 py-1.5 font-mono text-xs">{r.owner_user_id ?? "—"}</td>
                          <td className="px-2 py-1.5">{r.pack_id ?? "—"}</td>
                          <td className="px-2 py-1.5">{fmtAmount(r.amount_total, r.currency)}</td>
                          <td className="px-2 py-1.5 text-xs text-muted-foreground">
                            {new Date(r.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button
                  variant="default"
                  disabled={selected.size === 0}
                  onClick={() => setConfirmOpen(true)}
                >
                  Backfill {selected.size} selected
                </Button>
              </>
            )}
          </div>
        )}

        {batchResults && batchResults.length > 0 && (
          <div className="rounded-md border border-border bg-muted/30 p-3">
            <p className="mb-2 text-sm font-medium">Backfill results</p>
            <ul className="space-y-1 text-xs">
              {batchResults.map((r) => (
                <li key={r.session_id} className="flex items-baseline justify-between gap-3">
                  <span className="font-mono">{r.session_id}</span>
                  <span
                    className={
                      r.status === "credited"
                        ? "text-emerald-600"
                        : r.status === "already_credited"
                          ? "text-muted-foreground"
                          : "text-destructive"
                    }
                  >
                    {r.status}
                    {r.credits ? ` (+${r.credits})` : ""}
                    {r.error ? ` — ${r.error}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Backfill {selected.size} top-up session(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              Each session will have its credits posted to the user's ledger and a notification sent.
              Already-credited sessions are skipped automatically. Type <b>BACKFILL</b> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="BACKFILL"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== "BACKFILL" || running}
              onClick={(e) => {
                e.preventDefault();
                void runBackfill();
              }}
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
